import * as React from 'react';
import { FC, ReactNode } from 'react';

import { Box } from '@rbilabs/universal-components';
import { LayoutChangeEvent, Platform, ScrollView } from 'react-native';

import { LoadingAnimation } from 'components/lottie-animations/loading';
import { PullDownLoaderAnimation } from 'components/lottie-animations/pull-down-loader';
import { PullToReloadContent } from 'components/pull-to-reload/pull-to-reload-content';
import RefreshControl from 'components/pull-to-reload/refresh-control';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { isNative, isWeb } from 'utils/environment';
import noop from 'utils/noop';

interface IPullToReload {
  onRefresh(): Promise<any>;
  isRefreshing: boolean;
  minimumRefreshTime?: number;
  loadingAnimation?: ReactNode;
  isDisabled?: boolean;
  loadLazyData?: () => void;
  refreshMinimal?: boolean;
}

interface RefreshHeaderProps {
  height: string;
  isReleasing: boolean;
  refreshing: boolean;
  loadingAnimation?: ReactNode;
}

const ON_REFRESH_HEIGHT = 50;

const isWebScrollViewEvent = (
  ev: LayoutChangeEvent['nativeEvent']
): ev is LayoutChangeEvent['nativeEvent'] & { target: { parentNode: HTMLDivElement } } => {
  // @ts-ignore
  return isWeb && !!ev?.target?.parentNode;
};

export const RefreshHeader = ({
  height,
  isReleasing,
  refreshing,
  loadingAnimation,
}: RefreshHeaderProps) => {
  return (
    <Box
      height={refreshing ? ON_REFRESH_HEIGHT : height}
      paddingY={'$2'}
      position="absolute"
      width={'full'}
    >
      {!isReleasing && !refreshing && (
        <PullToReloadContent action="pullingDown" animationData={PullDownLoaderAnimation} />
      )}
      {!refreshing && isReleasing && (
        <PullToReloadContent action="releasing" animationData={PullDownLoaderAnimation} />
      )}
      {refreshing &&
        (loadingAnimation ? (
          loadingAnimation
        ) : (
          <PullToReloadContent action="refreshing" animationData={LoadingAnimation} />
        ))}
    </Box>
  );
};

export const PullToReload: FC<React.PropsWithChildren<IPullToReload>> = ({
  children,
  onRefresh,
  minimumRefreshTime,
  loadingAnimation,
  isRefreshing,
  isDisabled = false,
  loadLazyData = null,
  refreshMinimal = true,
}) => {
  const [pullLength, setPullLength] = React.useState<number>(0);
  const [isReleasing, setReleasing] = React.useState<boolean>(false);
  const [isRefreshingMinimal, setRefreshingMinimal] = React.useState<boolean>(false);

  const releaseLimit = Platform.select({ android: 0, ios: -120, web: 0 });
  const bottomOffset = Platform.select({ android: 100, ios: 0, web: 0 });
  const scrollEventThrottle = Platform.select({ android: 5, ios: 10, web: 10 });

  const minimumRefreshTimePromise = async () => {
    const minimumRefreshDuration = new Promise(res => {
      setRefreshingMinimal(refreshMinimal);
      setTimeout(res, minimumRefreshTime || 3000);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    }).then(_ => setRefreshingMinimal(false));
    // Mimicking Promise.allSettled so that this resolves even if refresh fails
    return Promise.all([onRefresh(), minimumRefreshDuration].map(promise => promise.catch(noop)));
  };

  const isMobile = useIsMobileBreakpoint();

  return isMobile && !isDisabled ? (
    <ScrollView
      nestedScrollEnabled
      showsVerticalScrollIndicator={false}
      onScroll={({ nativeEvent }) => {
        const y = nativeEvent.contentOffset.y;
        setPullLength(y);
        setReleasing(y <= (releaseLimit ?? 0));

        if (loadLazyData && isNative) {
          const { layoutMeasurement, contentOffset, contentSize } = nativeEvent;
          if (layoutMeasurement.height + contentOffset.y >= contentSize.height - 20) {
            loadLazyData();
          }
        }
      }}
      onLayout={({ nativeEvent }) => {
        // Force reset pull length for pull to reload initial state ( y >= 0 )
        if (nativeEvent.layout.y >= 0) {
          setPullLength(0);
        }

        // Fixes issue on RN WEB
        // RefreshControl adds an extra parent div that interferes with our layout
        if (isWebScrollViewEvent(nativeEvent)) {
          nativeEvent.target.parentNode.style.height = '100%';
        }
      }}
      scrollEventThrottle={scrollEventThrottle}
      refreshControl={
        <RefreshControl
          refreshing={isRefreshing}
          tintColor={'transparent'}
          onRefresh={minimumRefreshTimePromise}
        >
          <RefreshHeader
            height={`${pullLength * -1}px`}
            isReleasing={isReleasing}
            refreshing={isRefreshing || (refreshMinimal && isRefreshingMinimal)}
            loadingAnimation={loadingAnimation}
          />
        </RefreshControl>
      }
      // Using style instead of contentContainerStyle because Rewards page breaks otherwise
      style={{
        height: '100%',
      }}
      contentContainerStyle={{
        paddingBottom: bottomOffset,
      }}
    >
      {children}
    </ScrollView>
  ) : (
    <>{children}</>
  );
};
